/**
 * Thanksgiving labels — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingLabelsPack: ToilePack = {
    slug: 'thanksgiving-labels',
    category: 'season',
    name: 'Thanksgiving labels',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'buffet', label: 'Buffet labels', subject: 'A sheet of tent-fold food labels each with a leaf border and a blank centre, dashed cut lines'},
        {key: 'leftovers', label: 'Leftover labels', subject: 'A sheet of rectangular labels each lettered "LEFTOVERS" with blank lines for the dish and the date, dashed cut lines'},
        {key: 'pies', label: 'Pie labels', subject: 'A sheet of round labels each with a pie motif and a blank centre for the flavour, dashed cut lines'},
    ],
    styles: [
        {key: 'rustic', label: 'Rustic kraft', style: 'rustic printable on kraft paper, leaf and wheat borders, warm serif lettering, print-ready', palette: 'Kraft brown, rust, mustard and cream', framing: 'Laid out on a kraft page with an even margin'},
        {key: 'elegant', label: 'Elegant', style: 'elegant printable, thin gold line motifs, refined serif lettering, print-ready', palette: 'Cream, gold and a deep burgundy', framing: 'Laid out on a cream page with wide margins'},
        {key: 'kids', label: 'Kids', style: "kids' thanksgiving printable, cartoon turkeys and leaves, rounded lettering, print-ready", palette: 'Bright orange, yellow, red and brown', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for gift jars', 'with a date blank', 'as round stickers'],
    directionHint: 'try: for gift jars',
    seo: {
        title: 'Thanksgiving labels: buffet, leftovers and pie',
        metaDescription: "Nine Thanksgiving labels — buffet food tags, leftover labels, pie labels — blank centres, cut lines, rustic, elegant or kids' style.",
        h1: 'Thanksgiving labels: buffet, leftovers and pie in three styles',
        tagline: 'Nine label sheets, three uses, three styles each — blank centre, cut lines, name it yourself.',
        intro: "Three Thanksgiving label sheets: buffet food tags to name each dish, leftover labels for the fridge, pie labels for the dessert table. Every label leaves its centre blank for your own wording, with cut lines throughout. Drawn rustic burlap-and-kraft, elegant gold-and-cream, or bright kids' style. Print, cut, and write in the dish name.",
        useCases: [
            'a Thanksgiving buffet table with labeled dishes',
            'leftover storage labels for the fridge or freezer',
            'a pie table with each flavor marked',
            'a potluck where every dish needs a name tag',
        ],
        faq: [
            {q: 'Are the dish names already printed?', a: "No — every label's centre is left blank so you write in whatever dish or leftover it's actually naming."},
            {q: 'What size are the labels?', a: 'Each is drawn small enough to sit beside a serving dish or stick to a container, with a cut line around the edge.'},
            {q: 'Can I stick these to containers?', a: 'The labels print on plain paper — tape, a glue stick, or a piece of washi tape attaches them to a dish or container.'},
            {q: 'Is there a plainer version without the decorative border?', a: "The rustic set is the plainest of the three; elegant carries a gold border and kids' stays bright and playful."},
        ],
    },
}
